import { useContext, useEffect, useState } from 'react';
import { Box, Breadcrumbs, Typography, Table, TableContainer, Paper, TableHead, TableRow, TableCell, TableBody } from '@mui/material';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import { useToaster } from '../../../../providers/ToasterProvider';
import { ProjectContext } from '../../../../providers/projectProvider';
import { getPendingApprovalList } from '../../../../services/deviceExceptionService';
import { DeviceExceptionModal } from './DeviceExceptionModal';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import ACTION_VIEW from '../../../../assets/images/action-view.svg';

const columns = [
    { id: 'srNo', label: 'Sr No', minWidth: 80 },
    { id: 'labNo', label: 'Lab Number', minWidth: 140 },
    { id: 'seatNo', label: 'Seat Number', minWidth: 140 },
    { id: 'exceptionReason', label: 'Exception Reason', minWidth: 220 },
    { id: 'actions', label: 'Action', minWidth: 100 },
];

export const DeviceException = () => {

    const { projectDetails } = useContext(ProjectContext);
    const { addNotification } = useToaster();
    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);

    useEffect(() => {
        const fetchDevices = async () => {
            try {
                const res = await getPendingApprovalList();
                if (res?.code === 'OK') {
                    setDevices(Array.isArray(res.data) ? res.data : res.data?.rows ?? []);
                }
            } catch (error) {
                console.log('There is an error while fetching pending device exceptions', error);
            }
        }
        fetchDevices();
    }, []);

    const handleViewDevice = (rowIndex) => {
        setSelectedDevice(devices[rowIndex]);
    }

    const handleDecisionComplete = (deviceId, status) => {
        setDevices(devices.filter((device) => device.deviceId !== deviceId));
        setSelectedDevice(null);
        addNotification(`Device exception ${status} successfully.`, 'success');
    }

    const renderTableCell = (columnId, rowIndex) => {
        const row = devices[rowIndex];
        switch (columnId) {
            case 'srNo':
                return rowIndex + 1;
            case 'actions':
                return <Box component='span' sx={{ cursor: 'pointer' }} onClick={() => handleViewDevice(rowIndex)}>
                    <img src={ACTION_VIEW} alt='' />
                </Box>
            default:
                return row?.[columnId];
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>
                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>Device Exception</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container sx={{
                display: 'flex',
                flexDirection: 'column'
            }}>
                <h1 className='title'>Device Exception</h1>

                <Paper sx={{
                    width: '100%',
                    overflow: 'hidden',
                    bgcolor: 'background.base',
                    maxHeight: 'calc(100vh - 305px)',
                    maxWidth: 'calc(100vw - 140px)',
                    borderRadius: 1,
                    mb: 4
                }}>
                    <TableContainer sx={{
                        px: 1,
                        pb: 1,
                        maxHeight: 'inherit',
                        maxWidth: 'inherit',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: 1
                    }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            sx={{ color: 'primary.main', px: 1.5 }}
                                            key={column.id}
                                            align='center'
                                            style={{ minWidth: column.minWidth }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!devices?.length
                                    ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                        <TableCell colSpan={columns.length} sx={{ border: 'none', textAlign: 'center' }}>
                                            No records available
                                        </TableCell>
                                    </TableRow>
                                    : devices.map((row, rowIndex) => (
                                        <TableRow hover={false} role="checkbox" key={row.deviceId ?? rowIndex}>
                                            {columns.map((column) => (
                                                <TableCell key={column.id} align='center' sx={{ px: 1.5, py: 1 }}>
                                                    {renderTableCell(column.id, rowIndex)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <DeviceExceptionModal
                    device={selectedDevice}
                    onClose={() => setSelectedDevice(null)}
                    onDecisionComplete={handleDecisionComplete}
                />
            </Container>
        </MainContainer>
    );
}
